import React, { forwardRef, SyntheticEvent } from 'react'
import clsx from 'clsx'

import { getLabel } from 'helpers/language'
import { IconCheck } from '../Icons'

export interface CheckboxProps {
  id?: string
  name: string
  label?: string
  checked: boolean
  className?: string
  disabled?: boolean
  error?: boolean
  isLeft?: boolean
  isDropdown?: boolean
  variants?: 'primary' | 'secondary' | 'ternary'
  onChange?: (checked: boolean, e: SyntheticEvent) => void
}

const Checkbox: React.ForwardRefRenderFunction<HTMLDivElement, CheckboxProps> =
  (props: CheckboxProps, ref) => {
    const {
      checked,
      variants = 'primary',
      onChange = () => {},
      className,
      label,
      disabled = false,
      error = false,
      isLeft = false,
      isDropdown = false,
      ...others
    } = props

    const checkboxProps = {
      ...others,
      checked,
      className: clsx('checkbox', {
        [`checkbox--${variants}`]: variants,
        [`checkbox__checked--${variants}`]: checked,
        'checkbox--disabled': disabled,
        [`checkbox--error`]: error,
      }),
    }

    const _handleClick = (e: SyntheticEvent): void => {
      e.stopPropagation()

      if (disabled) {
        return undefined
      }

      onChange(!checked, e)
    }

    return (
      <div
        className={clsx(className, 'checkbox__wrapper', {
          'checkbox__wrapper--disabled': disabled,
          'checkbox__wrapper--left': isLeft,
          'checkbox__wrapper--dropdown': isDropdown,
        })}
        onClick={_handleClick}
        role='checkbox'
      >
        <div
          {...checkboxProps}
          ref={ref}
          data-testid={`checkbox-test-${variants}`}
        >
          {checked && !disabled && (
            <IconCheck className='icon--center' variants='xs' />
          )}
        </div>
        {label && (
          <span className={variants} data-testid='checkbox-label'>
            {getLabel(label)}
          </span>
        )}
      </div>
    )
  }

export default forwardRef<HTMLDivElement, CheckboxProps>(Checkbox)
